/**
 * The status header: where the applicant is, and the ONE thing to do next.
 *
 * TWO THINGS IT MUST NOT DO, both of them ruled elsewhere.
 *
 *   IT MUST NOT PROMISE A TURNAROUND. `work_queue_kind` seeds `application` with
 *   `sla_working_days = NULL` and `reserve_hours = NULL` — the column's own comment says "3 for
 *   manual_review; NULL = no SLA ruled" — and the overview says the same in words: an application
 *   SLA, if any, is a policy value and is not ruled. No operation on authors-api returns a queue
 *   position, a deadline or an estimate, so there is nothing here to count down and inventing one
 *   would be inventing a commitment.
 *
 *   IT MUST NOT SHOW A PERCENTAGE IT COMPUTED. Completeness is computed at read time by the
 *   server across five tables and is never stored on the profile; the SPA owns no business logic,
 *   so this percentage is the server's. The bar below reads `completenessPercent` and nothing else.
 *
 * AND THE PERCENTAGE IS NOT THE GATE. Four required rows are. So the header states both facts
 * separately rather than letting a bar imply that 100% is what unlocks the button.
 *
 * ★ DRAWN AS THE PROTOTYPE'S PAGE HEAD, IN A CALM CARD (the owner's live look at real cabinets,
 * 2026-10-01 / design 15): an icon for where things stand, the status pill, the page's one `<h1>`
 * at the prototype's title size with its line under it, the single action at the right, and the
 * percentage under a hairline. For an approved author inside the cabinet the title is the
 * prototype's own, "Your author profile" (`approvedStep`, ../domain/states.ts). The card's
 * measurements are in ./profile.module.css.
 */
import { Badge, Button, Group, Progress } from '@mantine/core';
import { BadgeCheck, CircleAlert, Clock, PenLine, Send } from '@moonx/ui';
import type { LucideIcon } from '@moonx/ui';
import type { ReactNode } from 'react';
import { useTranslation } from '../../../i18n';
import type { Application } from '../contract';
import { progressSummary, requiredOutstanding, rowsGateSending } from '../domain/completeness';
import type { NextStep } from '../domain/states';
import classes from './profile.module.css';

const TONE_COLOUR: Record<NextStep['tone'], string> = {
  // ★ m2, NOT m5, AND m3 IS THE TRAP HERE. This badge paints its ink on the accent tint
  // (`#281f0c`), not on a surface, and that tint is light enough to eat a rung and a half.
  // Measured 2026-09-23 by compositing and reading the pixel: m5 3.79 (axe reported 3.78 live),
  // m4 4.11, m3 4.46 - JUST under the 4.5 `docs/ui/01-tokens.md` rules, which is exactly the
  // margin an eye would wave through - and m2 5.02. The dimmest rung that actually clears wins.
  neutral: 'oklch(var(--mx-m2))',
  // ★ THE GOLD WORDS TAKE `accent-hover`, AND "APPROVED" TAKES THE TEXT INK (design 15). On the
  // light card's tint `accent` measured 3.77 and `success` 4.43 - and "Approved" is the pill every
  // approved author sees on this page. `accent-hover` reads 5.09 light / 11.55 dark there, the text
  // ink 13.4 / 15.3; the green stays in the card's frame (`TONE_FRAME`, at the foot).
  ready: 'oklch(var(--mx-accent-hover))',
  waiting: 'oklch(var(--mx-info))',
  attention: 'oklch(var(--mx-warning-ink))', // the INK; the frame keeps `warning` - toneBorder
  good: 'oklch(var(--mx-text))',
};

const TONE_LABEL = {
  neutral: 'status.tone.neutral',
  ready: 'status.tone.ready',
  waiting: 'status.tone.waiting',
  attention: 'status.tone.attention',
  good: 'status.tone.good',
} as const satisfies Record<NextStep['tone'], string>;

/** The icon beside the title, `aria-hidden`: the pill and the title say the same in words. */
const TONE_ICON: Record<NextStep['tone'], LucideIcon> = {
  neutral: PenLine,
  ready: Send,
  waiting: Clock,
  attention: CircleAlert,
  good: BadgeCheck,
};

export interface StatusHeaderProps {
  readonly application: Application;
  readonly step: NextStep;
  /** Runs the single action. Disabled while a call is in flight. */
  readonly onAction: () => void;
  readonly busy: boolean;
  /** Rendered under the action — for example "submitting is held until the agreement is accepted". */
  readonly note?: ReactNode;
}

export function StatusHeader({
  application,
  step,
  onAction,
  busy,
  note,
}: StatusHeaderProps): ReactNode {
  const { t } = useTranslation('application');
  const outstanding = requiredOutstanding(application);
  const percent = application.completenessPercent;
  const ToneIcon = TONE_ICON[step.tone];

  return (
    <section
      className={classes.status}
      aria-labelledby="apply-status-title"
      style={{ borderColor: `color-mix(in oklab, ${toneBorder(step.tone)} 45%, transparent)` }}
    >
      <div className={classes.statusTop}>
        <span className={classes.statusIcon} aria-hidden="true">
          <ToneIcon size={19} />
        </span>

        <div className={classes.statusText}>
          <Group gap="xs">
            <Badge
              variant="light"
              style={{ color: TONE_COLOUR[step.tone] }}
              aria-label={t('status.ariaLabel', { status: t(TONE_LABEL[step.tone]) })}
            >
              {t(TONE_LABEL[step.tone])}
            </Badge>
            {application.round !== null && application.round !== undefined ? (
              <Badge variant="default">
                {application.round === 1
                  ? t('status.firstRound')
                  : t('status.round', { round: application.round })}
              </Badge>
            ) : null}
          </Group>
          <h1 className={classes.pageTitle} id="apply-status-title">
            {step.title}
          </h1>
          <p className={classes.lede}>{step.body}</p>
        </div>

        {step.action !== null ? (
          <div className={classes.statusAction}>
            <Button
              size="md"
              radius="xl"
              loading={busy}
              onClick={onAction}
              variant={step.action.kind === 'fill_in' ? 'light' : 'filled'}
            >
              {step.action.label}
            </Button>
            {note}
          </div>
        ) : (
          <div className={classes.statusAction}>
            {/* "Nothing to do right now" is a wait's words; a profile is not waiting on anything. */}
            {step.situation === 'approved' ? null : (
              <span className={classes.quiet} style={{ textAlign: 'right' }}>
                {t('status.nothingToDo')}
              </span>
            )}
            {note}
          </div>
        )}
      </div>

      <div className={classes.progress}>
        <Progress
          value={percent}
          aria-label={t('status.completenessLabel', { percent })}
          size="sm"
          radius="xl"
        />
        <Group justify="space-between" gap="xs">
          <span className={classes.quiet}>{progressSummary(application, t)}</span>
          {application.completenessAtSubmit !== null &&
          application.completenessAtSubmit !== undefined ? (
            <span className={classes.quiet}>
              {t('status.atSubmit', { percent: application.completenessAtSubmit })}
            </span>
          ) : null}
        </Group>
        {/* "Not what decides this" is about an application. An approved author's profile has
            nothing left to decide, so the percentage above stands alone (design 14, F3). */}
        {outstanding === 0 && percent < 100 && rowsGateSending(application) ? (
          <span className={classes.quiet}>{t('status.restOptional')}</span>
        ) : null}
      </div>
    </section>
  );
}

/** The frame's tone: the tone's own colour, which is a border and not text - see `toneBorder`. */
const TONE_FRAME: Record<NextStep['tone'], string> = {
  neutral: 'oklch(var(--mx-m2))',
  ready: 'oklch(var(--mx-accent))',
  waiting: 'oklch(var(--mx-info))',
  attention: 'oklch(var(--mx-warning))',
  good: 'oklch(var(--mx-success))',
};

/**
 * THE FRAME'S COLOUR, WHICH IS THE TONE'S OWN AND NOT ALWAYS ITS INK. `TONE_COLOUR` is the
 * badge's words, and `attention`'s words take the warning INK - web/ui's per-scheme alias, light
 * `accentHover` and dark `warning` - because light `warning` on the accent tint read 4.05; the ink
 * reads 6.14, dark 8.56 either way (web/authors/test/tinted-inks.spec.tsx). A border is not text,
 * so the frame stays `warning`. Since design 15 the gold and the green words take darker inks too
 * (`TONE_COLOUR`), so the frame reads every tone from `TONE_FRAME` above.
 */
function toneBorder(tone: NextStep['tone']): string {
  return TONE_FRAME[tone];
}
